import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import toast from "react-hot-toast";
import moment from "moment";
import CityDateService from "../Services/CityDateService";

const headLineStyle = {
  textAlign: "center",
  fontSize: "30px",
  color: "#868e96",
  margin: "15px",
  fontWeight: "bold",
};

function ManageCityDate() {
  const [cityDates, setCityDates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [editCityName, setEditCityName] = useState("");
  const [editRawDate, setEditRawDate] = useState("");

  const columns = [
    {
      name: "S.No.",
      selector: (row, index) => index + 1,
      sortable: true,
      width: "90px",
    },
    {
      name: "City Name",
      selector: (row) =>
        row.city && typeof row.city === "object"
          ? row.city.name
          : row.cityName || row.city || "N/A",
      sortable: true,
    },
    {
      name: "Date",
      selector: (row) => row.date || "N/A",
      sortable: true,
    },
    {
      name: "Created Date",
      selector: (row) =>
        row.createdAt
          ? moment(row.createdAt).format("DD-MM-YYYY")
          : row.createdOn
          ? moment(row.createdOn).format("DD-MM-YYYY")
          : "N/A",
      sortable: true,
    },
    {
      name: "Action",
      cell: (row) => (
        <div>
          <button
            title="Edit Date"
            onClick={() => handleEdit(row)}
            className="btn btn-sm me-2"
            style={{
              background: "transparent",
              border: "none",
              color: "rgb(133, 218, 79)",
            }}
          >
            <i className="fa-solid fa-pen-to-square fs-5"></i>
          </button>
          <button
            title="Delete"
            onClick={() => handleDelete(row._id || row.id)}
            className="btn btn-sm"
            style={{
              background: "transparent",
              border: "none",
              color: "#dc3545",
            }}
          >
            <i className="fa-solid fa-trash-can fs-5"></i>
          </button>
        </div>
      ),
      center: true,
    },
  ];

  const fetchCityDates = async () => {
    setLoading(true);
    try {
      const res = await CityDateService.getAllCityDates();
      console.log("CityDates data:", res);
      if (res?.data && Array.isArray(res.data)) {
        setCityDates(res.data);
      } else if (Array.isArray(res)) {
        setCityDates(res);
      } else {
        setCityDates([]);
      }
    } catch (error) {
      console.error("Error fetching city dates:", error);
      toast.error("Failed to load City Dates!");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCityDates();
  }, []);

  const handleEdit = (row) => {
    setEditId(row._id || row.id);
    const cityName =
      row.city && typeof row.city === "object"
        ? row.city.name
        : row.cityName || row.city || "";
    setEditCityName(cityName);

    // Convert DD-MM-YYYY date string to YYYY-MM-DD for date input
    if (row.date) {
      const parsed = moment(row.date, ["DD-MM-YYYY", "YYYY-MM-DD"]);
      if (parsed.isValid()) {
        setEditRawDate(parsed.format("YYYY-MM-DD"));
      } else {
        setEditRawDate("");
      }
    } else {
      setEditRawDate("");
    }

    setIsEdit(true);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Delete Record?",
      text: "Are you sure you want to delete this City Date record?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      confirmButtonColor: "#d33",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        const res = await CityDateService.deleteCityDate(id);
        if (res?.status !== false) {
          Swal.fire("Deleted!", "Record has been deleted.", "success");
          fetchCityDates();
        } else {
          toast.error(res?.message || "Failed to delete record");
        }
      } catch (error) {
        Swal.fire("Error!", "Failed to delete record.", "error");
      }
    }
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();

    if (!editRawDate) {
      toast.error("Please select a date.");
      return;
    }

    const formattedDate = moment(editRawDate).format("DD-MM-YYYY");

    try {
      const payload = {
        date: formattedDate,
      };

      const res = await CityDateService.updateCityDate(editId, payload);

      if (res?.status !== false) {
        toast.success("Date updated successfully!");
        setIsEdit(false);
        setEditId(null);
        setEditCityName("");
        setEditRawDate("");
        fetchCityDates();
      } else {
        toast.error(res?.message || "Failed to update date.");
      }
    } catch (error) {
      toast.error("Failed to update date.");
      console.error("Error updating date:", error);
    }
  };

  const handleCancelEdit = () => {
    setIsEdit(false);
    setEditId(null);
    setEditCityName("");
    setEditRawDate("");
  };

  return (
    <div className="main_wrap" style={{ marginTop: "6rem" }}>
      <div className="container-fluid">
        {isEdit ? (
          <section className="chartBox_area">
            <div className="card">
              <div className="card-header">
                <h4 className="mb-0">Edit City Date</h4>
              </div>
              <div className="card-body">
                <form onSubmit={handleUpdateSubmit}>
                  <div className="row">
                    <div className="col-md-6 mt-3">
                      <label>City Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editCityName}
                        disabled
                      />
                      <small className="text-muted">
                        City cannot be changed. Only the date can be updated.
                      </small>
                    </div>

                    <div className="col-md-6 mt-3">
                      <label htmlFor="editDatePicker">
                        Select New Date<span className="text-danger">*</span>
                      </label>
                      <input
                        type="date"
                        id="editDatePicker"
                        className="form-control"
                        value={editRawDate}
                        onChange={(e) => setEditRawDate(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="mt-4 pt-3 border-top">
                    <button
                      type="submit"
                      className="btn me-3"
                      style={{
                        background: "rgb(133, 218, 79)",
                        border: "none",
                        padding: "10px 25px",
                        fontWeight: "500",
                      }}
                    >
                      <i className="fa-solid fa-floppy-disk me-2"></i>
                      Update Date
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      className="btn btn-secondary"
                      style={{
                        padding: "10px 25px",
                        fontWeight: "500",
                      }}
                    >
                      <i className="fa-solid fa-times me-2"></i>
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </section>
        ) : (
          <div className="mt-5">
            <div style={headLineStyle} className="page-headline">
              Manage City Dates
            </div>
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading City Dates...</p>
              </div>
            ) : cityDates.length > 0 ? (
              <div className="card">
                <div className="card-body">
                  <DataTable
                    columns={columns}
                    data={cityDates}
                    pagination
                    responsive
                    highlightOnHover
                    striped
                    paginationPerPage={10}
                    paginationRowsPerPageOptions={[10, 20, 30, 50]}
                    noDataComponent={
                      <div className="text-center py-4">
                        <i className="fa-solid fa-calendar-xmark fa-2x text-muted mb-3"></i>
                        <p>No City Dates found</p>
                      </div>
                    }
                  />
                </div>
              </div>
            ) : (
              <div className="card">
                <div className="card-body text-center py-5">
                  <i className="fa-solid fa-calendar-xmark fa-3x text-muted mb-3"></i>
                  <h5>No City Dates Available</h5>
                  <p className="text-muted">
                    There are no City Date records to display at the moment.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ManageCityDate;
